import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Bell, Info, Calendar, MessageSquare, CheckCircle, AlertTriangle,
  Loader2, CheckCheck, Trash2
} from "lucide-react";
import {
  getNotifications, getNotificationCount,
  markNotificationRead, markAllNotificationsRead, deleteNotification
} from "@/lib/api";

const REFRESH_INTERVAL = 30000; // 30 seconds

const typeConfig = {
  info: { icon: Info, color: "text-blue-600 bg-blue-50", label: "Informacja" },
  deadline: { icon: Calendar, color: "text-orange-600 bg-orange-50", label: "Termin" },
  comment: { icon: MessageSquare, color: "text-violet-600 bg-violet-50", label: "Komentarz" },
  success: { icon: CheckCircle, color: "text-emerald-600 bg-emerald-50", label: "Sukces" },
  warning: { icon: AlertTriangle, color: "text-amber-600 bg-amber-50", label: "Ostrzezenie" },
};

function timeAgo(dateString) {
  if (!dateString) return "";
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "teraz";
  if (diffMin < 60) return `${diffMin} min temu`;
  if (diffHour < 24) return `${diffHour} godz. temu`;
  if (diffDay < 7) return `${diffDay} dn. temu`;
  return date.toLocaleDateString("pl-PL");
}

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const intervalRef = useRef(null);

  // Fetch unread count
  const fetchCount = useCallback(async () => {
    try {
      const res = await getNotificationCount();
      setUnreadCount(res.data?.count ?? res.data ?? 0);
    } catch {
      // Silently ignore count fetch errors
    }
  }, []);

  // Fetch notifications list
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getNotifications();
      setNotifications(res.data || []);
    } catch {
      setError("Nie udało się pobrać powiadomień");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh count every 30 seconds
  useEffect(() => {
    fetchCount();
    intervalRef.current = setInterval(fetchCount, REFRESH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchCount]);

  // Fetch notifications on popover open
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const handleMarkRead = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Silently ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently ignore
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      const removed = notifications.find((n) => n.id === notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (removed && !removed.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // Silently ignore
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markNotificationRead(notification.id).catch(() => {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    // krok 2 audytu 02.09: powiadomienie „Znaleziono N terminów" prowadzi do panelu propozycji
    // (`/project/{id}/sources?terminy=1`) — dotąd `link` był ignorowany
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[380px] p-0" align="end" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">Powiadomienia</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5">
                {unreadCount} nowe
              </Badge>
            )}
          </div>
          {notifications.some((n) => !n.read) && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={handleMarkAllRead}
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Oznacz wszystkie
            </Button>
          )}
        </div>

        {/* Notifications list */}
        <ScrollArea className="max-h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={fetchNotifications}>
                Spróbuj ponownie
              </Button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Brak powiadomien</p>
              <p className="text-xs text-muted-foreground mt-1">
                Nowe powiadomienia pojawia sie tutaj
              </p>
            </div>
          ) : (
            <div>
              {notifications.map((notification, i) => {
                const config = typeConfig[notification.type] || typeConfig.info;
                const TypeIcon = config.icon;
                return (
                  <div key={notification.id || i}>
                    {i > 0 && <Separator />}
                    <button
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                        !notification.read ? "bg-accent/30" : ""
                      }`}
                    >
                      {/* Type icon */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${config.color}`}>
                        <TypeIcon className="h-4 w-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-tight ${!notification.read ? "font-semibold" : "font-medium"}`}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                          )}
                        </div>
                        {notification.message && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] text-muted-foreground">
                            {timeAgo(notification.created_at)}
                          </span>
                          {!notification.read && (
                            <button
                              onClick={(e) => handleMarkRead(e, notification.id)}
                              className="text-[10px] text-primary hover:underline"
                            >
                              Oznacz jako przeczytane
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDelete(e, notification.id)}
                        className="text-muted-foreground/50 hover:text-destructive transition-colors shrink-0 mt-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
